/**
 * Tabs.
 *
 * The ledger version: a hairline runs the full width of the container and the
 * selected tab thickens its own segment of that line. No pill, no filled
 * track, no card. The selection is carried by weight and by the line, which is
 * the same mechanism the rest of the product uses for hierarchy.
 *
 * Roving tabindex with the standard keyboard map: arrows move and select,
 * Home and End jump to the ends, Tab leaves the list. Only the selected tab is
 * in the tab order, which is what stops a five tab row from costing a keyboard
 * user five presses to get past it.
 */

import type { ReactNode } from 'react';
import { useId, useRef } from 'react';
import '../../styles/components/tabs.css';

export interface TabItem {
  id: string;
  label: string;
  /** Mono, right of the label. A count, never a word. */
  count?: string;
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Names the tab list for a screen reader. */
  label: string;
  children?: ReactNode;
}

export function Tabs({ tabs, value, onChange, label, children }: TabsProps) {
  const base = useId();
  const listRef = useRef<HTMLDivElement | null>(null);

  function focusTab(index: number) {
    const next = tabs[(index + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.id);
    const node = listRef.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(base + next.id)}`);
    node?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const index = tabs.findIndex((tab) => tab.id === value);
    if (index === -1) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusTab(index + 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusTab(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTab(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTab(tabs.length - 1);
    }
  }

  return (
    <div className="tabs">
      <div
        className="tabs__list"
        role="tablist"
        aria-label={label}
        ref={listRef}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => {
          const selected = tab.id === value;
          return (
            <button
              key={tab.id}
              type="button"
              id={base + tab.id}
              className="tabs__tab"
              role="tab"
              aria-selected={selected}
              aria-controls={selected ? `${base}panel` : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.id)}
            >
              <span className="tabs__label">{tab.label}</span>
              {tab.count ? <span className="tabs__count mono">{tab.count}</span> : null}
            </button>
          );
        })}
      </div>
      {children ? (
        <div className="tabs__panel" role="tabpanel" id={`${base}panel`} tabIndex={0}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
